import { MagazineContextProvider } from '@/contexts/Magazine/Magazine.context';
import useMeasure from '@/hooks/useMeasure/useMeasure';
import useScroll from '@/hooks/useScroll/useScroll';
import { animated, useSpring } from '@react-spring/web';
import Button from '@uikit/components/Button/Button';
import {
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  ShowIcon,
} from '@uikit/components/Button/consts';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import Clickable from '../../Clickable/Clickable';
import { ClickableAction } from '../../Clickable/consts';
import Icon from '../../Icon/Icon';
import ProgressBar from '../../ProgressBar/ProgressBar';
import type { MagazineArticleHeaderProps } from './interfaces';
import styles from './MagazineArticleHeader.module.scss';

const MagazineArticleHeader = ({
  barVisible,
  articleTitle,
  category = 'our-world',
  categorySlug,
  categoryTitle,
  articleAnchors = [],
}: MagazineArticleHeaderProps) => {
  const [open, setOpen] = useState(false);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const heightRef = useRef<HTMLDivElement>(null);
  const { height } = useMeasure(heightRef);

  const heightAnimProps = useSpring({
    height: open ? height : 0,
    config: {
      mass: 1,
      tension: 100,
      friction: 16,
      clamp: height === 0,
    },
    delay: 100,
  });

  const navChildren = [...articleAnchors].map(({ title, anchor }) => (
    <div key={title + anchor} className={styles.anchorItemWrapper}>
      <Clickable
        onClick={() => setOpen(!open)}
        anchor={anchor}
        key={title + anchor}
        actionType={ClickableAction.Anchor}
        className={styles.anchorItem}
      >
        {title}
      </Clickable>
    </div>
  ));

  useEffect(() => {
    if (isScrolledToTop) {
      setOpen(false);
    }
  }, [isScrolledToTop]);

  useScroll(() => {
    open && setOpen(false);
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const updateScrollState = () => {
        setIsScrolledToTop(window.scrollY <= 96);
        setScrollProgress(
          ((window.scrollY + window.innerHeight) * 100) /
            document.body.clientHeight
        );
      };
      window.addEventListener('scroll', updateScrollState, { passive: true });
      updateScrollState();
      return () => {
        window.removeEventListener('scroll', updateScrollState);
      };
    }
  }, []);

  return (
    <header
      className={clsx(styles.base, {
        [styles.isScrolledTop]: isScrolledToTop,
      })}
    >
      <MagazineContextProvider value={{ currentCategory: category }}>
        <div className={styles.topWrapper}>
          <div className={styles.articleTitleWrapper}>
            <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
              <Clickable
                actionType={ClickableAction.Linkintern}
                linkintern="/magazin"
                className={clsx(styles.link, styles.mainLink)}
              >
                Magazin
              </Clickable>
              <Icon
                size="iconSize16"
                variant={'action/chevron-right'}
                className={styles.chevron}
              />
              <Clickable
                actionType={ClickableAction.Linkintern}
                linkintern={categorySlug}
                className={clsx(styles.link, styles.categoryColor)}
              >{`#${categoryTitle}`}</Clickable>
              <Icon
                size="iconSize16"
                variant={'action/chevron-right'}
                className={clsx(styles.chevron, styles.categoryColor)}
              />
              <button
                title="Menü öffnen / schließen"
                className={clsx(styles.linkButton, styles.link)}
                onClick={() => {
                  !isScrolledToTop && setOpen(!open);
                }}
                aria-current="page"
                disabled={articleAnchors.length < 1}
              >
                <span>{articleTitle}</span>

                {articleAnchors.length > 0 && !isScrolledToTop && (
                  <div className={styles.iconWrapper}>
                    <Icon
                      variant={'action/chevron-right'}
                      size="iconSize16"
                      className={clsx(styles.icon, {
                        [styles.rotated]: open,
                      })}
                    />
                  </div>
                )}
              </button>
            </nav>
          </div>
          <Button
            actionType={ClickableAction.Linkintern}
            linkintern={'/magazin'}
            color={ButtonColor.Primary}
            width={ButtonWidth.Auto}
            size={ButtonSize.Small}
            icon={'action/arrow-left'}
            showIcon={ShowIcon.ShowIcon}
            clickContext={'Page Header'}
            className={clsx(styles.backButton, {
              [styles.backButtonScrolled]: !isScrolledToTop,
            })}
          >
            Übersicht
          </Button>

          {articleAnchors.length > 0 && !isScrolledToTop && (
            <button
              title="Menü öffnen / schließen"
              className={styles.navButtonMobile}
              onClick={() => setOpen(!open)}
            >
              <span>Inhalt</span>
              <Icon
                variant={'action/chevron-right'}
                size="iconSize20"
                className={clsx(styles.icon, {
                  [styles.rotated]: open,
                })}
              />
            </button>
          )}
        </div>
        {barVisible && (
          <ProgressBar
            progress={scrollProgress}
            visible={barVisible}
            className={styles.progressBar}
          />
        )}
        <animated.div
          className={clsx(styles.heightAnimator, {
            [styles.closed]: !open,
          })}
          style={heightAnimProps}
        >
          <div ref={heightRef}>
            <div className={styles.children}>
              <ul className={styles.childrenList}>{navChildren}</ul>
            </div>
          </div>
        </animated.div>
        <div
          className={clsx(styles.overlay, {
            [styles.overlayIsActive]: open,
          })}
          onClick={() => setOpen(!open)}
        />
      </MagazineContextProvider>
    </header>
  );
};

export default MagazineArticleHeader;
